import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client';
import { courseKeys } from './useCourses';
import type { CourseDetail, CourseModule, ModuleProgress } from '../api/types';

/**
 * Lesson query hooks — backed by `services/course/handler.py`.
 *
 * Until the backend ships a dedicated lesson-body endpoint, we resolve a
 * lesson by fetching the parent course detail and selecting the requested
 * module — one network round-trip serves both the course detail page and
 * the lesson page. The lesson body comes straight from `course_modules.body`
 * via the course-detail payload.
 *
 * `useMarkLessonComplete` hits the existing REST route
 *   POST /courses/{slug}/modules/{moduleSlug}/complete
 * (see services/course/handler.py:_REST_ROUTES), which publishes
 * `course.module.completed` on the event bus and updates `module_progress`.
 */

export interface LessonBody {
  /** Lesson body source format. `markdown` is rendered by `LessonView`'s
   *  minimal renderer; `text` is rendered as preformatted plain text;
   *  `pdf` is embedded as an iframe via `pdfUrl`. */
  format: 'markdown' | 'text' | 'pdf';
  /** Raw body content. Ignored when `format === 'pdf'`. */
  source: string;
  /** Optional PDF URL when `format === 'pdf'`. */
  pdfUrl?: string;
}

export interface LessonNeighbour {
  /** Module id used to build the route `/courses/{courseSlug}/{moduleId}`. */
  id: string;
  title: string;
}

export interface LessonViewData {
  /** Parent course identity, used for breadcrumbs + navigation. */
  course: { slug: string; title: string };
  /** The module being viewed. */
  module: CourseModule;
  /** Position in the module list, 1-indexed, for UI display. */
  position: number;
  /** Total module count, for the "Lesson N of M" header. */
  total: number;
  /** Lesson body — theory content, with optional PDF override. */
  body: LessonBody;
  /** Recommended next steps surfaced on the lesson page. */
  prev: LessonNeighbour | null;
  next: LessonNeighbour | null;
  /** Trainee progress on this module. */
  progress: ModuleProgress;
}

export const lessonKeys = {
  all: ['lessons'] as const,
  detail: (courseSlug: string, moduleId: string) =>
    [...lessonKeys.all, courseSlug, moduleId] as const,
};

/**
 * Type-guard for the optional body shape we accept off the wire. The
 * `course_modules.body` column may be a string (markdown source) or a
 * structured `{ format, source, pdfUrl }` envelope when authored.
 */
function coerceLessonBody(input: unknown): LessonBody {
  if (input && typeof input === 'object' && 'format' in input && 'source' in input) {
    const obj = input as { format: string; source: unknown; pdfUrl?: unknown };
    const format = obj.format === 'pdf' || obj.format === 'text' ? obj.format : 'markdown';
    const source = typeof obj.source === 'string' ? obj.source : '';
    const pdfUrl = typeof obj.pdfUrl === 'string' ? obj.pdfUrl : undefined;
    return pdfUrl !== undefined ? { format, source, pdfUrl } : { format, source };
  }
  if (typeof input === 'string') {
    return { format: 'markdown', source: input };
  }
  return { format: 'markdown', source: '' };
}

export function useLesson(courseSlug: string | undefined, moduleId: string | undefined) {
  return useQuery<LessonViewData>({
    queryKey: lessonKeys.detail(courseSlug ?? '', moduleId ?? ''),
    enabled: Boolean(courseSlug) && Boolean(moduleId),
    queryFn: async ({ signal }) => {
      const course = await apiFetch<CourseDetail>('/courses/{slug}', {
        method: 'GET',
        pathParams: { slug: courseSlug ?? '' },
        signal,
      });
      const idx = course.modules.findIndex((m) => m.id === moduleId);
      if (idx === -1) throw new Error(`Lesson not found: ${courseSlug}/${moduleId}`);
      const mod = course.modules[idx];
      if (!mod) throw new Error(`Lesson not found: ${courseSlug}/${moduleId}`);
      const prev = idx > 0 ? course.modules[idx - 1] ?? null : null;
      const next = idx < course.modules.length - 1 ? course.modules[idx + 1] ?? null : null;
      const bodyField = (mod as unknown as { body?: unknown }).body;
      const progressField =
        (mod as unknown as { progress?: ModuleProgress }).progress ??
        ({ moduleId: mod.id, status: 'not_started', completedAt: null, score: null } as ModuleProgress);
      return {
        course: { slug: course.id, title: course.title },
        module: mod,
        position: idx + 1,
        total: course.modules.length,
        body: coerceLessonBody(bodyField),
        prev: prev ? { id: prev.id, title: prev.title } : null,
        next: next ? { id: next.id, title: next.title } : null,
        progress: progressField,
      };
    },
    staleTime: 60_000,
  });
}

export interface MarkCompleteInput {
  courseSlug: string;
  moduleId: string;
}

export interface MarkCompleteResponse {
  ok: boolean;
  moduleId: string;
  completedAt: string;
}

export function useMarkLessonComplete() {
  const qc = useQueryClient();
  return useMutation<MarkCompleteResponse, Error, MarkCompleteInput>({
    mutationFn: async ({ courseSlug, moduleId }) =>
      apiFetch<MarkCompleteResponse>(
        '/courses/{slug}/modules/{moduleSlug}/complete',
        {
          method: 'POST',
          pathParams: { slug: courseSlug, moduleSlug: moduleId },
          body: { course_slug: courseSlug, module_slug: moduleId },
        },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: courseKeys.detail(vars.courseSlug) });
      qc.invalidateQueries({ queryKey: lessonKeys.detail(vars.courseSlug, vars.moduleId) });
    },
  });
}
